/**
 * Single source of truth for the list of Sentinel-deployed countries
 * inside the portal app.
 *
 * The portal itself is per-country at runtime (VITE_COUNTRY_CODE pins
 * the build to one country), but several portal surfaces need to know
 * about ALL deployed countries:
 *   - Super-admin country switcher in AdminLayout
 *   - Login / forgot / reset password pages (display "DK login" etc.)
 *   - POS property country dropdown
 *
 * Adding a new country = ONE entry here. Anywhere else that hardcodes
 * `{ZA: 'South Africa', DK: 'Denmark'}` is a bug — search for the
 * country code or country name before adding new code.
 */
export interface KnownCountry {
  /** Uppercase ISO 3166-1 alpha-2 (matches VITE_COUNTRY_CODE casing). */
  code: string;
  /** English display name. */
  label: string;
  /** Public portal URL for this country. */
  url: string;
}

export const KNOWN_COUNTRIES: readonly KnownCountry[] = [
  { code: 'DK', label: 'Denmark', url: 'https://dk.app.cleanmybed.com' },
  { code: 'ZA', label: 'South Africa', url: 'https://za.app.cleanmybed.com' },
];

/** HQ is a separate admin portal; it sits next to country portals in the switcher. */
export const HQ_PORTAL = {
  code: 'HQ',
  label: 'Global Admin',
  url: 'https://global.app.cleanmybed.com',
} as const;

/** Country-code → display-name map. Returns code uppercased if not found. */
export function getCountryName(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  const match = KNOWN_COUNTRIES.find((c) => c.code === upper);
  return match ? match.label : upper;
}

/** Build a `<select>`-style options list of all known countries. */
export function getCountryOptions(): Array<{ value: string; label: string }> {
  return KNOWN_COUNTRIES.map((c) => ({ value: c.code, label: c.label }));
}
